import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  HomeIcon,
  ChartBarIcon,
  CubeIcon,
  BeakerIcon,
  ClockIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const Layout = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: HomeIcon },
    { name: 'Sales Sheet', href: '/app/sales', icon: ChartBarIcon },
    { name: 'Inventory Sheet', href: '/app/inventory', icon: CubeIcon },
    { name: 'AI Analysis', href: '/app/ai-analysis', icon: BeakerIcon },
    { name: 'Scheduler', href: '/app/scheduler', icon: ClockIcon },
    { name: 'Profile', href: '/app/profile', icon: UserCircleIcon },
    { name: 'Pricing', href: '/app/pricing', icon: CurrencyDollarIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 border-r border-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
          <Link to="/app/dashboard" className="text-xl font-bold flex items-center text-white">
            <div className="bg-white/10 p-1.5 rounded-lg backdrop-blur-md mr-2 border border-white/20">
              <svg className="h-5 w-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            Innventory<span className="text-amber-400">AI</span>
          </Link>
          <button
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1.5 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`${isActive
                  ? 'bg-amber-500/10 text-amber-400 border-r-4 border-amber-500'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  } group flex items-center px-3 py-2.5 text-sm font-medium rounded-l-md transition-all duration-200`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon
                  className={`${isActive ? 'text-amber-400' : 'text-gray-500 group-hover:text-gray-300'
                    } mr-3 flex-shrink-0 h-5 w-5 transition-colors`}
                />
                {item.name}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-800 bg-gray-900">
          <button
            onClick={logout}
            className="group flex w-full items-center px-3 py-2.5 text-sm font-medium rounded-md text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200"
          >
            <ArrowRightOnRectangleIcon className="mr-3 flex-shrink-0 h-5 w-5 text-gray-500 group-hover:text-red-400 transition-colors" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 lg:ml-0">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>

              {/* Breadcrumb or page title */}
              <div className="ml-4 lg:ml-0">
                <h2 className="text-xl text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-purple-700 font-extrabold capitalize tracking-tight">
                  {location.pathname.split('/').pop().replace('-', ' ') || 'Dashboard'}
                </h2>
              </div>
            </div>

            <div className="flex items-center space-x-3 sm:space-x-6">
              {/* Credits & User Info */}
              {user && (
                <div className="flex items-center space-x-4">
                  <div className="hidden sm:flex items-center bg-gradient-to-r from-indigo-50 to-purple-50 px-3 py-1.5 rounded-full border border-indigo-100/60 shadow-inner">
                    <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider mr-2">
                      {user.tier} Plan
                    </span>
                    <div className="h-4 w-px bg-indigo-200 mx-2"></div>
                    <span className="text-sm text-gray-600 mr-1">Credits:</span>
                    <span className="text-sm font-extrabold text-indigo-900">{user.credits}</span>
                  </div>

                  <div className="flex items-center pl-2 border-l border-gray-200 space-x-3">
                    <div className="hidden md:flex flex-col text-right">
                      <span className="text-sm font-bold text-gray-900 leading-none">{user.full_name || user.username}</span>
                      <span className="text-xs text-gray-500 font-medium leading-none mt-1">{user.email}</span>
                    </div>
                    <div className="h-9 w-9 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-md cursor-pointer hover:ring-2 hover:ring-offset-2 hover:ring-blue-500 transition-all">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications */}
              <button className="relative p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors">
                <BellIcon className="h-6 w-6" />
                <span className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;